// Here are all the Events from the theme you can use

/**
 * CART CHANGE
 * Use this to get notified whenever the cart changes. Either via products add or change in the cart itself
 *
 * document.addEventListener("cart:changed", (event) => {
 *  const { cart_items } = event.detail;
 * });
 *
 * PRODUCT ADDED
 * Use this to get notified whenever the customer adds a variant via the ATC Button.
 *
 * document.addEventListener("product:added", (event) => {
 *  const { variant, quantity } = event.detail;
 * });
 *
 * VARIANT CHANGE
 * Use this to get notified whenever the customer changes the variant of a product. This only works for products that have a variant switcher
 * and not a switcher between parent products, since then we have a page reload anyway
 *
 * document.addEventListener("variant:changed", (event) => {
 *  const { variant, previousVariant } = event.detail;
 * });
 *
 * REFRESH MINICART
 * Use this to refresh the mini-cart after you change it.
 *
 * document.documentElement.dispatchEvent(
 *  new CustomEvent("cart:refresh", {
 *    bubbles: true,
 *  })
 * );
 **/

// CART HANDLE FUNCTIONS

/**
 * Updates the cart quantities for specified product variants.
 *
 * @function updateCart
 * @param {Object} updates - An object containing variant IDs and their corresponding quantities.
 * @returns {Promise<void>}
 */
function updateCart(updates) {
  return fetch("/cart/update.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({ updates }),
  })
    .then((response) => response.json())
    .then(() => {
      // Dispatch a custom event to refresh the cart UI
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", { bubbles: true })
      );
    })
    .catch((error) => {
      console.error("Failed to update cart:", error);
    });
}

/**
 * Adds items to the cart.
 *
 * @function addItemsToCart
 * @param {Array<number>} variantIds - An array of variant IDs to add to the cart.
 * @returns {Promise<void>}
 */
function addItemsToCart(variantIds) {
  const items = variantIds.map((id) => ({ id, quantity: 1 }));

  return fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({ items }),
  })
    .then((response) => response.json())
    .then(() => {
      // Dispatch a custom event to refresh the cart UI
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", { bubbles: true })
      );
    })
    .catch((error) => {
      console.error("Failed to add products to cart:", error);
    });
}

/**
 * Removes specified items from the cart.
 *
 * @function removeItemsFromCart
 * @param {Array<number>} variantIds - An array of variant IDs to remove from the cart.
 * @returns {Promise<void>}
 */
function removeItemsFromCart(variantIds) {
  const updates = variantIds.reduce((acc, id) => {
    acc[id] = 0;
    return acc;
  }, {});

  return fetch("/cart/update.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({ updates }),
  })
    .then((response) => response.json())
    .then(() => {
      // Dispatch a custom event to refresh the cart UI
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", { bubbles: true })
      );
    })
    .catch((error) => {
      console.error("Failed to remove products from cart:", error);
    });
}

/**
 * Clears all items from the cart.
 *
 * @function clearCart
 * @returns {Promise<void>}
 */
function clearCart() {
  return fetch("/cart/clear.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
  })
    .then((response) => response.json())
    .then(() => {
      // Dispatch a custom event to refresh the cart UI
      // We have to set a timeout otherwise the cart will be cleared but the refresh wont work as expected
      setTimeout(() => {
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", { bubbles: true })
        );
      }, 500);
    })
    .catch((error) => {
      console.error("Failed to clear the cart:", error);
    });
}

/**
 * Handles removal of hidden products from the cart.
 *
 * @event cart:changed
 */
document.addEventListener("cart:changed", (event) => {
  const hideProductData = document.querySelector("[data-hide-product-json]")
    ? JSON.parse(document.querySelector("[data-hide-product-json]").innerHTML)
    : null;

  const goodieData = document.querySelector("[data-goodie-json]")
    ? JSON.parse(document.querySelector("[data-goodie-json]").innerHTML)
    : null;

  const itemsToRemove = event.detail.cart.items.filter((item) =>
    hideProductData.includes(item.id)
  );

  if (itemsToRemove.length > 0 && !goodieData.goodiesActivated) {
    const idsToRemove = itemsToRemove.map((item) => item.id);
    removeItemsFromCart(idsToRemove);
  }
});

/**
 * Custom element for managing cart goodies based on cart value thresholds.
 *
 * @class CartGoodies
 * @extends HTMLElement
 */
var CartGoodies = class extends HTMLElement {
  connectedCallback() {
    this.handleCartChange = this.handleCartChange.bind(this);
    this.goodieData = document.querySelector("[data-goodie-json]")
      ? JSON.parse(document.querySelector("[data-goodie-json]").innerHTML)
      : null;

    this.goodieGroups = this.goodieData.goodies.reduce((acc, obj) => {
      const key = obj["threshold"];
      let group = acc.find((item) => item["threshold"] === key);
      if (!group) {
        group = { ["threshold"]: key, items: [] };
        acc.push(group);
      }
      group.items.push(obj);
      return acc;
    }, []);

    this.boundHandleCartChange = (event) => this.handleCartChange(event);
    document.addEventListener("cart:changed", this.boundHandleCartChange);
  }

  disconnectedCallback() {
    document.removeEventListener("cart:changed", this.boundHandleCartChange);
  }

  handleCartChange(event) {
    const cartItems = event.detail.cart.items;
    const cartTotal = event.detail.cart.total_price;

    // Check if there are any physical products in cart
    const hasPhysicalProducts = cartItems.some((item) => item.requires_shipping);

    // If only digital products, don't activate goodies
    if (!hasPhysicalProducts) {
      return;
    }

    const updates = {};
    this.goodieGroups.forEach((group) => {
      group.items.forEach((goodie) => {
        // Here are the general goodies that dont belong to a threshold order.
        // This works for automatic and manual goodies
        if (goodie.is_general_goodie) {
          goodie.goodie_variants.forEach((variantId) => {
            const isInCart = cartItems.some((item) => item.id === variantId.id);
            if (cartTotal >= goodie.threshold) {
              if (!isInCart) {
                updates[variantId.id] = 1;
              }
            } else {
              if (isInCart) {
                updates[variantId.id] = 0;
              }
            }
          });
        }
      });
    });

    // Now, ensure only the highest eligible non-general, automatic goodie is in the cart
    const nonGeneralAutos = this.goodieData.goodies.filter(
      (goodie) => !goodie.is_general_goodie && goodie.selection === "Automatic"
    );
    let eligibleGoodie = null;
    let maxThreshold = -Infinity;
    nonGeneralAutos.forEach((goodie) => {
      if (goodie.threshold <= cartTotal && goodie.threshold > maxThreshold) {
        eligibleGoodie = goodie;
        maxThreshold = goodie.threshold;
      }
    });
    nonGeneralAutos.forEach((goodie) => {
      goodie.goodie_variants.forEach((variant) => {
        const isInCart = cartItems.some((item) => item.id === variant.id);
        if (eligibleGoodie && goodie === eligibleGoodie) {
          if (!isInCart) updates[variant.id] = 1;
        } else {
          if (isInCart) updates[variant.id] = 0;
        }
      });
    });

    cartItems.forEach((item) => {
      const isGoodie = this.goodieData.goodies.some((goodie) =>
        goodie.goodie_variants.some((variant) => variant.id === item.id)
      );
      if (isGoodie && item.quantity > 1) {
        updates[item.id] = 1;
      }
    });

    const allGoodies = cartItems.every((item) =>
      this.goodieData.goodies.some((goodie) =>
        goodie.goodie_variants.some((variant) => variant.id === item.id)
      )
    );

    if (allGoodies || cartTotal === 0) {
      clearCart();
      return;
    }

    if (Object.keys(updates).length > 0) {
      updateCart(updates);
    }
  }
};
window.customElements.define("cart-goodies", CartGoodies);

// PROGRESS BAR

var ProgressBar = class extends HTMLElement {
  connectedCallback() {
    document.addEventListener("cart:changed", this._onCartUpdated.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener(
      "cart:changed",
      this._onCartUpdated.bind(this)
    );
  }

  get currentThreshold() {
    return parseFloat(this.getAttribute("threshold"));
  }

  get prevThreshold() {
    return parseFloat(this.getAttribute("prev-threshold"));
  }

  _onCartUpdated(event) {
    const cartItems = event.detail.cart.items;
    const hasPhysicalProducts = cartItems.some((item) => item.requires_shipping);

    // If only digital products, don't update progress
    if (!hasPhysicalProducts) {
      return;
    }

    const totalPrice = event.detail.cart.total_price;
    const progress =
      (totalPrice - this.prevThreshold) /
      (this.currentThreshold - this.prevThreshold);
    this.style.setProperty("--progress", Math.max(0, Math.min(progress, 1)));
  }
};
window.customElements.define("progress-bar", ProgressBar);

// SLIDER ELEMENT

class SliderElement extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.swiperConfig = this.dataset.swiperConfig
      ? JSON.parse(this.dataset.swiperConfig)
      : null;
  }

  connectedCallback() {
    const swiper = new Swiper(this, this.swiperConfig);
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

customElements.define("slider-element", SliderElement);

// QUICK FORM

class QuickForm extends HTMLElement {
  constructor() {
    super();
    this.atcButton = this.querySelector(".QuickAddToCart");
    this.variantSelect = this.querySelector("select");

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    this.atcButton.addEventListener("click", this.handleSubmit);
  }

  disconnectedCallback() {
    this.atcButton.removeEventListener("click", this.handleSubmit);
  }

  handleSubmit(event) {
    event.preventDefault();

    this.atcButton.setAttribute("disabled", "disabled");
    document.dispatchEvent(new CustomEvent("theme:loading:start")); // Then we add the product in Ajax

    const payload = {
      items: [
        {
          quantity: this.querySelector('[name="quantity"]').value,
          id: this.querySelector('[name="id"]').value,
        },
      ],
    };

    fetch("".concat(window.routes.cartAddUrl, ".js"), {
      body: JSON.stringify(payload),
      credentials: "same-origin",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest", // This is needed as currently there is a bug in Shopify that assumes this header
      },
    }).then((response) => {
      document.dispatchEvent(new CustomEvent("theme:loading:end"));

      if (response.ok) {
        this.atcButton.removeAttribute("disabled"); // We simply trigger an event so the mini-cart can re-render

        fetch("".concat(window.routes.cartUrl, ".js"), {
          credentials: "same-origin",
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        })
          .then((response) => {
            return response.json();
          })
          .then((data) => {
            document.dispatchEvent(
              new CustomEvent("cart:changed", {
                bubbles: true,
                detail: {
                  cart: data,
                },
              })
            );
          })
          .then(() => {
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:refresh", {
                bubbles: true,
              })
            );
          });
      }
    });
  }
}

customElements.define("quick-form", QuickForm);

/**
 * Step-by-step variant selection logic
 * This creates a guided selection process where users select one option at a time
 */
function initMinimalStepVariantLogic() {
  const productFormVariants = document.querySelector('.ProductForm__Variants');
  if (!productFormVariants) return;
  const requireAllOptions = productFormVariants.hasAttribute('data-require-all-options') && productFormVariants.getAttribute('data-require-all-options') === 'true';
  if (!requireAllOptions) return;

  const optionContainers = Array.from(document.querySelectorAll('.ProductForm__Option'));
  if (optionContainers.length <= 1) return;

  // Store original add-to-cart button text
  const addToCartButton = document.querySelector('.ProductForm__AddToCart');
  let originalAddToCartText = addToCartButton ? addToCartButton.innerHTML : '';

  // Helper: check if an option is selected (radio, select, swatch)
  function isOptionSelected(container) {
    // Radio
    const radioButton = container.querySelector('input[type="radio"]:checked');
    if (radioButton) return true;
    // Select
    const select = container.querySelector('select');
    if (select) {
      // Check for empty value or placeholder
      if (!select.value || select.value === '' || select.value === select.getAttribute('placeholder')) return false;
      return true;
    }
    // Swatch/Custom
    const selectedValue = container.querySelector('.ProductForm__SelectedValue');
    if (selectedValue) {
      const text = selectedValue.textContent && selectedValue.textContent.trim();
      // Use translation key if available
      const placeholder = window.languages?.productFormSelectOption || '';
      if (!text || text === '' || (placeholder && text.toLowerCase() === placeholder.toLowerCase())) return false;
      return true;
    }
    return false;
  }

  // Find the first unselected option index
  function firstUnselectedIndex() {
    for (let i = 0; i < optionContainers.length; i++) {
      if (!isOptionSelected(optionContainers[i])) return i;
    }
    return -1;
  }

  // Show only up to the first unselected option, or all if all selected
  function updateOptionVisibility() {
    const firstUnselected = firstUnselectedIndex();
    if (firstUnselected === -1) {
      // All selected: show all, enable add-to-cart
      optionContainers.forEach((container) => {
        container.classList.remove('is-hidden');
      });
      enableAddToCartButton();
    } else {
      // Show up to and including the first unselected
      optionContainers.forEach((container, idx) => {
        if (idx <= firstUnselected) {
          container.classList.remove('is-hidden');
        } else {
          container.classList.add('is-hidden');
        }
      });
      disableAddToCartButton();
    }
  }

  function enableAddToCartButton() {
    if (addToCartButton) {
      addToCartButton.removeAttribute('disabled');
      addToCartButton.setAttribute('data-action', 'add-to-cart');
      addToCartButton.classList.remove('Button--secondary');
      addToCartButton.classList.add('Button--primary');
      // Do NOT set innerHTML here, let Shopify handle the label
    }
  }
  function disableAddToCartButton() {
    if (addToCartButton) {
      addToCartButton.setAttribute('disabled', 'disabled');
      addToCartButton.removeAttribute('data-action');
      addToCartButton.classList.remove('Button--primary');
      addToCartButton.classList.add('Button--secondary');
      addToCartButton.innerHTML = window.languages?.productFormSelectVariant || 'Bitte Variante auswählen';
    }
  }

  // Listen for option changes (radio, select, swatch)
  document.addEventListener('change', function(event) {
    if (
      event.target.matches('input[name^="option-"]') ||
      event.target.matches('select[name^="option-"]')
    ) {
      setTimeout(updateOptionVisibility, 50);
    }
  });
  document.addEventListener('click', function(event) {
    if (event.target.matches('.VariantSelector__Button, .SizeSwatch__Radio, .ColorSwatch__Radio, [data-action="select-variant"]')) {
      setTimeout(updateOptionVisibility, 100);
    }
  });

  // Debounced observer for product form only
  let debounceTimer = null;
  const productForm = productFormVariants.closest('.ProductForm') || productFormVariants;
  const observer = new MutationObserver(() => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      initMinimalStepVariantLogic();
    }, 150);
  });
  observer.observe(productForm, { subtree: true, childList: true });

  // Initial state
  updateOptionVisibility();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMinimalStepVariantLogic);
} else {
  initMinimalStepVariantLogic();
}
document.addEventListener('shopify:section:load', function(event) {
  if (event.target.classList.contains('ProductSection')) {
    setTimeout(initMinimalStepVariantLogic, 100);
  }
});
let lastUrl = location.href;
// Remove global MutationObserver, now handled per product form

/**
 * Utility: Disable focus and hide containers site‑wide.
 * @param {Object} config
 * @param {string[]} config.focusSelectors     CSS selectors for elements to disable focus on (tabindex="-1").
 * @param {string[]} config.containerSelectors CSS selectors for containers to set aria-hidden="true" and role="presentation".
 * @returns {MutationObserver} Observer instance, so you can disconnect later if needed.
 */
function disableFocusAndHide({ focusSelectors = [], containerSelectors = [] }) {
  if (!focusSelectors.length && !containerSelectors.length) {
    throw new Error('Provide at least one selector');
  }

  const focusQuery     = focusSelectors.join(', ');
  const containerQuery = containerSelectors.join(', ');

  const disableFocus = el => {
    if (el.getAttribute('tabindex') !== '-1') {
      el.setAttribute('tabindex', '-1');
    }
  };

  const hideContainer = el => {
    if (el.getAttribute('aria-hidden') !== 'true') {
      el.setAttribute('aria-hidden', 'true');
    }
    if (el.getAttribute('role') !== 'presentation') {
      el.setAttribute('role', 'presentation');
    }
  };

  // Initial pass
  if (focusQuery) document.querySelectorAll(focusQuery).forEach(disableFocus);
  if (containerQuery) document.querySelectorAll(containerQuery).forEach(hideContainer);

  // Observer for dynamic changes
  const observer = new MutationObserver(mutations => {
    const toDisable = new Set();
    const toHide    = new Set();

    mutations.forEach(m => {
      if (m.type === 'childList') {
        m.addedNodes.forEach(node => {
          if (!(node instanceof HTMLElement)) return;
          if (focusQuery && node.matches(focusQuery)) toDisable.add(node);
          if (focusQuery) node.querySelectorAll(focusQuery).forEach(el => toDisable.add(el));
          if (containerQuery && node.matches(containerQuery)) toHide.add(node);
          if (containerQuery) node.querySelectorAll(containerQuery).forEach(el => toHide.add(el));
        });
      } else if (m.type === 'attributes' && m.attributeName === 'aria-hidden') {
        const target = m.target;
        if (containerQuery && target.matches(containerQuery)) {
          toHide.add(target);
          if (focusQuery) target.querySelectorAll(focusQuery).forEach(el => toDisable.add(el));
        }
      }
    });

    toDisable.forEach(disableFocus);
    toHide.forEach(hideContainer);
  });

  observer.observe(document.body, {
    childList:      true,
    subtree:        true,
    attributes:     true,
    attributeFilter:['aria-hidden']
  });

  return observer;
}

// -----------------------------------------------------------------------------
// Usage: disable focus and hide for Experify reviews plugin
// -----------------------------------------------------------------------------
(function() {
  const config = {
    focusSelectors: [],
    containerSelectors: [
      '#experify-button-div',
      '#ReviewsWidget'
    ]
  };

  // Ensure script runs after initial DOM is ready
  function init() {
    window.experifyObserver = disableFocusAndHide(config);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run on dynamic section reloads
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.querySelector('.experify-reviews')) {
      if (window.experifyObserver) window.experifyObserver.disconnect();
      init();
    }
  });
})();
